import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { SUCCESS } from 'utils/constants/queryStatuses';
import { Intake, EXPAND_FIRST, SECONDARY_THEME } from 'new-ui/Components/Intake/Intake';
import { INTERCOM_EVENTS } from 'modules/intercom/intercom';
import { PopupTitle } from 'new-ui/Components/Popup';
import { useTranslation } from 'utils/modifiedTranslation';
import app from 'new-ui/app';
import inContextEvents, {
  GA_CATEGORY_NAME,
} from 'modules/ga/inContextEvents';
import './MissingCriticalInformation.css';

const BACKDROP_REASON = 'missingCriticalInformation';

const getQuestionsFromMatches = ({ trials, treatments }) => [...trials, ...treatments].map(
  ({ missed_questions: questions }) => questions,
).flat();

// There are next cases:
// - CASE 1: questions prop is passed
// - 1.1: popup opening is triggered by parameter from url
// e.g. /dashboard/results?popup=missing_critical_information&source=bot&popup_data=%7B%22questions%22%3A%5B%22biomarkers%22%2C%22ecog%22%2C%22have_insurance%22%2C%22insurances%22%5D%7D
// - 1.2: popup opening is triggered by clicking "Missing critical information"
// - CASE 2: question prop is not passed
export const MissingCriticalInformation = ({ source, questions: questionsProp }) => {
  const { t } = useTranslation();
  const { current: answeredQuestions } = useRef({});
  const sourceRef = useRef(source);
  const { status: matchesStatus, trials, treatments } = useSelector((state) => state.matches);
  const { data: profile } = useSelector((state) => state.profile);

  const [questions, setQuestions] = useState(() => {
    // there is no need to deduplicate or filter questions by condition (filterQuestionsByCondition)
    if (Array.isArray(questionsProp) && questionsProp.length) {
      return questionsProp;
    }
    if (matchesStatus === SUCCESS) {
      return getQuestionsFromMatches({ trials, treatments });
    }
    return null;
  });

  const isReady = Array.isArray(questions);
  const questionsCount = questions?.length ?? 0;

  useEffect(() => {
    if (isReady || matchesStatus !== SUCCESS) {
      return;
    }
    setQuestions(getQuestionsFromMatches({ trials, treatments }));
  }, [isReady, matchesStatus, treatments, trials]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    const { eventName, getMetaData } = inContextEvents.popupInContextDisplayed;
    app.sendGoogleAnalyticsEvent(
      GA_CATEGORY_NAME,
      eventName,
      getMetaData(questionsCount),
    );
  }, [questionsCount, isReady]);

  // onPopupClose
  useEffect(() => () => {
    app.intercom.sendEvent(INTERCOM_EVENTS.MISSING_CRITICAL_INFORMATION.POPUP_SUCCESS, { source: sourceRef.current });
  }, []);

  if (!isReady) {
    return null;
  }

  return (
    <div className="missing-critical-information-popup">
      <PopupTitle title={t('missing_critical.title')} />
      <Intake
        preventNotSure
        shouldRenderDoneButton={false}
        id="missingCriticalInformationPopup"
        expand={EXPAND_FIRST}
        theme={SECONDARY_THEME}
        user={profile}
        targetQuestions={questions}
        onUpdate={async ({ user, question, isQuestionAnswered }) => {
          app.showPreloader({ reason: BACKDROP_REASON });
          await app.handleUserUpdate({ data: user });
          app.hidePreloader({ reason: BACKDROP_REASON });

          answeredQuestions[question] = isQuestionAnswered;
          if (questions.every((questionId) => answeredQuestions[questionId])) {
            app.closePopups();
          }
        }}
      />
    </div>
  );
};

MissingCriticalInformation.propTypes = {
  questions: PropTypes.arrayOf(PropTypes.string),
  source: PropTypes.string,
};
